import type { BuilderStep, LastActiveStepConfig, StepCompletion } from "../config";
import { BUILDER_STEPS } from "./stepState";

/**
 * Map the derived step completion to the persisted `lastActiveStep` cursor value.
 *
 * Tracks the furthest CONTIGUOUSLY completed step (a later step only counts when
 * every prior step is complete, mirroring reachability). The terminal/submitted
 * value is owned by the submit flow, so a channel omits its final step from
 * `byCompletedStep` and this tops out at the last pre-submit step.
 */
export const deriveLastActiveStep = (completion: StepCompletion, config: LastActiveStepConfig): string => {
  let value = config.initial;
  let priorComplete = true;
  for (const step of BUILDER_STEPS) {
    priorComplete = priorComplete && completion[step];
    if (!priorComplete) break;
    const mapped = config.byCompletedStep[step];
    if (mapped) value = mapped;
  }
  return value;
};

export const resumeStepFromCursor = (stored: string | null, config: LastActiveStepConfig): BuilderStep => {
  if (!stored || stored === config.initial) return BUILDER_STEPS[0];
  if (config.terminalValues?.includes(stored)) return BUILDER_STEPS[BUILDER_STEPS.length - 1];
  const entry = Object.entries(config.byCompletedStep).find(([, value]) => value === stored);
  if (!entry) return BUILDER_STEPS[0];
  const completedIdx = BUILDER_STEPS.indexOf(entry[0] as BuilderStep);
  return BUILDER_STEPS[Math.min(completedIdx + 1, BUILDER_STEPS.length - 1)];
};
